import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { buildSearchIndex } from '../lib/fuzzyTrie';

const {
  FiSearch, FiExternalLink, FiFolder, FiPlus, FiCpu,
  FiDownload, FiMoon, FiSun, FiTrash2, FiX, FiCommand,
  FiShield, FiBookOpen, FiColumns, FiMaximize2, FiHelpCircle,
  FiSliders, FiActivity, FiClock
} = FiIcons;

export default function CommandPalette({
  isOpen,
  onClose,
  data,
  settings,
  onOpenBookmark,
  onOpenFolder,
  onNavigateFolder,
  onAddBookmark,
  onAddFolder,
  onOpenAI,
  onAuditDuplicates,
  onExportJSON,
  onExportHTML,
  onToggleTheme,
  onOpenVault,
  onOpenReading,
  onToggleZen,
  onToggleViewMode,
  onOpenShortcuts,
  accentColor = '#c2ffe0',
  themeColors: tc,
  isDark: isDarkProp,
}) {
  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : settings?.isDark !== false && !tc?.card?.toLowerCase().includes('ffffff');

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Flatten all bookmarks
  const allBookmarks = useMemo(() => {
    return Object.values(data).flatMap((folder) => {
      const items = folder.items || [];
      return items
        .filter((item) => item.type === 'link')
        .map((item) => ({
          ...item,
          folderTitle: folder.id === 'root' ? 'Home' : folder.title,
          folderId: folder.id,
        }));
    });
  }, [data]);

  // Flatten all folders
  const allFolders = useMemo(() => {
    return Object.values(data).filter((f) => f.id !== 'root');
  }, [data]);

  // Sub-millisecond Fuzzy Trie Index
  const searchIndex = useMemo(() => {
    return buildSearchIndex(allBookmarks, []);
  }, [allBookmarks]);

  // Filtered items
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const actionList = [
      {
        id: 'action-add-bm',
        type: 'action',
        title: 'Add New Bookmark',
        subtitle: 'Create a new link in current folder',
        icon: FiPlus,
        action: () => { onClose(); onAddBookmark(); },
      },
      {
        id: 'action-add-folder',
        type: 'action',
        title: 'Add New Folder',
        subtitle: 'Create a new category folder',
        icon: FiFolder,
        action: () => { onClose(); onAddFolder(); },
      },
      {
        id: 'action-open-ai',
        type: 'action',
        title: 'Ask AI Assistant',
        subtitle: 'Chat or automate your bookmark space',
        icon: FiCpu,
        action: () => { onClose(); onOpenAI(); },
      },
      {
        id: 'action-vault',
        type: 'action',
        title: 'Open Zero-Knowledge Vault (Alt+V)',
        subtitle: 'AES-256 client encrypted passwords and secret keys',
        icon: FiShield,
        action: () => { onClose(); onOpenVault?.(); },
      },
      {
        id: 'action-reading',
        type: 'action',
        title: 'Open Reading Queue (Alt+R)',
        subtitle: 'Articles and bookmarks queued to read',
        icon: FiBookOpen,
        action: () => { onClose(); onOpenReading?.(); },
      },
      {
        id: 'action-zen',
        type: 'action',
        title: 'Toggle Zen Focus Mode (Alt+Z)',
        subtitle: 'Minimalist distraction-free dashboard',
        icon: FiMaximize2,
        action: () => { onClose(); onToggleZen?.(); },
      },
      {
        id: 'action-view-mode',
        type: 'action',
        title: 'Cycle View Mode',
        subtitle: 'Toggle between Grid, List, and Kanban views',
        icon: FiColumns,
        action: () => { onClose(); onToggleViewMode?.(); },
      },
      {
        id: 'action-shortcuts',
        type: 'action',
        title: 'Keyboard Shortcuts Guide (?)',
        subtitle: 'View all keyboard navigation shortcuts',
        icon: FiHelpCircle,
        action: () => { onClose(); onOpenShortcuts?.(); },
      },
      {
        id: 'action-audit',
        type: 'action',
        title: 'Audit Duplicate & Broken Links',
        subtitle: 'Scan library for duplicate URLs and clean up',
        icon: FiTrash2,
        action: () => { onClose(); onAuditDuplicates(); },
      },
      {
        id: 'action-export-json',
        type: 'action',
        title: 'Export Library to JSON',
        subtitle: 'Full database backup',
        icon: FiDownload,
        action: () => { onClose(); onExportJSON(); },
      },
      {
        id: 'action-export-html',
        type: 'action',
        title: 'Export to Netscape HTML',
        subtitle: 'Chrome / Firefox / Safari compatible file',
        icon: FiDownload,
        action: () => { onClose(); onExportHTML(); },
      },
      {
        id: 'action-toggle-theme',
        type: 'action',
        title: settings?.isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
        subtitle: 'Toggle dashboard appearance',
        icon: settings?.isDark ? FiSun : FiMoon,
        action: () => { onClose(); onToggleTheme(); },
      },
    ];

    if (!q) {
      return [
        ...actionList,
        ...allBookmarks.slice(0, 8).map((b) => ({
          id: `bm-${b.id}`,
          type: 'bookmark',
          title: b.title,
          subtitle: `${b.folderTitle} • ${b.url}`,
          url: b.url,
          logoUrl: b.logoUrl,
          icon: FiExternalLink,
          action: () => { onOpenBookmark(b.url); onClose(); },
        })),
      ];
    }

    const matchedActions = actionList.filter((a) =>
      a.title.toLowerCase().includes(q) || a.subtitle.toLowerCase().includes(q)
    );

    const navigateFn = onOpenFolder || onNavigateFolder;
    const matchedFolders = allFolders
      .filter((f) => f.title.toLowerCase().includes(q))
      .map((f) => ({
        id: `folder-${f.id}`,
        type: 'folder',
        title: f.title,
        subtitle: `Folder with ${(f.items || []).length} item(s)`,
        icon: FiFolder,
        action: () => { navigateFn?.(f.id); onClose(); },
      }));

    const trieMatches = searchIndex.query(q, 15);
    const trieBookmarks = trieMatches
      .filter((m) => m.item?.type === 'bookmark')
      .map((m) => {
        const b = m.item;
        return {
          id: `bm-${b.id}`,
          type: 'bookmark',
          title: b.title,
          subtitle: `${b.folderTitle || 'Library'} • ${b.url}`,
          url: b.url,
          logoUrl: b.logoUrl,
          icon: FiExternalLink,
          action: () => { onOpenBookmark(b.url); onClose(); },
        };
      });

    // Fallback if trie returned fewer than 5
    const fallbackBookmarks = allBookmarks
      .filter((b) =>
        b.title.toLowerCase().includes(q) ||
        (b.url && b.url.toLowerCase().includes(q)) ||
        (b.note && b.note.toLowerCase().includes(q))
      )
      .slice(0, 10)
      .map((b) => ({
        id: `bm-${b.id}`,
        type: 'bookmark',
        title: b.title,
        subtitle: `${b.folderTitle} • ${b.url}`,
        url: b.url,
        logoUrl: b.logoUrl,
        icon: FiExternalLink,
        action: () => { onOpenBookmark(b.url); onClose(); },
      }));

    const seenIds = new Set();
    const finalBookmarks = [];
    [...trieBookmarks, ...fallbackBookmarks].forEach((bm) => {
      if (!seenIds.has(bm.id)) {
        seenIds.add(bm.id);
        finalBookmarks.push(bm);
      }
    });

    return [...matchedActions, ...matchedFolders, ...finalBookmarks];
  }, [query, allBookmarks, allFolders, settings, searchIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    function handleKeyDown(e) {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % (items.length || 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + items.length) % (items.length || 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = items[selectedIndex];
        if (selected?.action) selected.action();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, items, selectedIndex, onClose]);

  // Auto scroll to active index
  useEffect(() => {
    if (listRef.current) {
      const activeEl = listRef.current.children[selectedIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/70 backdrop-blur-md"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className={`relative w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden z-10 border ${
            isDark ? 'bg-[#121214] border-[#26262a]' : 'bg-white border-slate-200'
          }`}
        >
          {/* Top Search Bar */}
          <div className={`flex items-center gap-3 px-4 py-3.5 border-b ${isDark ? 'border-[#222226]' : 'border-slate-200'}`}>
            <SafeIcon icon={FiSearch} className={`w-5 h-5 ${isDark ? 'text-[#777]' : 'text-slate-400'}`} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Type a command, search links, or jump to folder..."
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDark ? 'text-white placeholder-[#555]' : 'text-slate-900 placeholder-slate-400'
              }`}
            />
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                isDark ? 'bg-[#1e1e22] text-[#777] border-[#2c2c32]' : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}>
                Esc
              </span>
              <button
                onClick={onClose}
                className={`p-1 rounded-lg transition-colors cursor-pointer ${
                  isDark ? 'text-[#666] hover:text-white hover:bg-[#222]' : 'text-slate-400 hover:text-slate-800 hover:bg-slate-100'
                }`}
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Results List */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto p-2 space-y-1">
            {items.length === 0 ? (
              <div className={`py-12 text-center text-sm ${isDark ? 'text-[#555]' : 'text-slate-400'}`}>
                No matching bookmarks, folders, or actions found.
              </div>
            ) : (
              items.map((item, idx) => {
                const isSelected = idx === selectedIndex;
                return (
                  <div
                    key={item.id}
                    onClick={() => item.action()}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all ${
                      isSelected
                        ? isDark
                          ? 'bg-[#1e1e24] text-white shadow-sm'
                          : 'bg-slate-100 text-slate-900 shadow-sm'
                        : isDark
                          ? 'text-[#aaa] hover:bg-[#18181c]'
                          : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {/* Icon / Favicon */}
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        item.type === 'action'
                          ? isDark ? 'bg-[#25252c] text-[#c2ffe0]' : 'bg-emerald-50 text-emerald-600'
                          : item.type === 'folder'
                          ? 'bg-amber-500/10 text-amber-500'
                          : isDark ? 'bg-[#222]' : 'bg-slate-100'
                      }`}
                    >
                      {item.logoUrl ? (
                        <img
                          src={item.logoUrl}
                          alt=""
                          className="w-4 h-4 rounded object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <SafeIcon icon={item.icon} className="w-4 h-4" />
                      )}
                    </div>

                    {/* Titles */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {item.title}
                      </div>
                      <div className={`text-[11px] truncate ${isDark ? 'text-[#666]' : 'text-slate-500'}`}>
                        {item.subtitle}
                      </div>
                    </div>

                    {/* Tag badge */}
                    <div className="shrink-0">
                      <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                        isDark ? 'bg-[#18181c] text-[#666] border-[#26262a]' : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Navigation Hints */}
          <div className={`px-4 py-2 border-t flex items-center justify-between text-[11px] ${
            isDark ? 'border-[#202024] bg-[#0c0c0e] text-[#555]' : 'border-slate-200 bg-slate-50 text-slate-500'
          }`}>
            <div className="flex items-center gap-3">
              <span><strong className={isDark ? 'text-[#888]' : 'text-slate-700'}>↑↓</strong> Navigate</span>
              <span><strong className={isDark ? 'text-[#888]' : 'text-slate-700'}>↵</strong> Open</span>
              <span><strong className={isDark ? 'text-[#888]' : 'text-slate-700'}>Esc</strong> Exit</span>
            </div>
            <div className={`flex items-center gap-1 ${isDark ? 'text-[#444]' : 'text-slate-400'}`}>
              <SafeIcon icon={FiCommand} className="w-3.5 h-3.5" />
              <span>MeMark Palette</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
